import { useMemo } from 'react';

import { useTranslation } from '@mint-lib/i18n';

import { getTranslatedGoalGroups } from '../../../../../../constants/campaignBuilder.js';
import { useGetMediaPlanRows } from '../../../../../../hooks/useGetMediaPlanRows.js';
import { MediaPlanRow } from '../../../../../../types/campaignBuilder.js';
import { Goal, GOAL_GROUPS, GoalOption } from './types.js';
import { getPath } from './utils.js';

type UseGoalOptionListArgs = {
  columnMeta: { [key: string]: Goal[] };
  isTileRow: boolean;
  parentId: number;
};

const usePreparedGoalOptions = ({
  columnMeta,
  isTileRow,
  parentId,
}: UseGoalOptionListArgs): GoalOption[] => {
  const { t } = useTranslation('@myn/planning');

  const preparedRows = useGetMediaPlanRows();

  const campaignsGoals = useMemo(() => {
    if (!columnMeta || !isTileRow) {
      return [];
    }

    const [currentChannel, currentPlatform] = preparedRows.reduce(
      (acc: string[], item: MediaPlanRow) => {
        if (acc.length) {
          return acc;
        }
        const platform = item.subRows?.find((subRow) => subRow.id === parentId);
        return platform ? [item.name, platform.name] : acc;
      },
      [],
    );

    const path = getPath(currentChannel, currentPlatform);

    const translatedGoalGroups = getTranslatedGoalGroups(t);

    const goalGroups = translatedGoalGroups.map((group) => ({
      label: group.label,
      id: group.id,
      goal: columnMeta[group.id],
    }));

    const goals = [
      {
        goal: columnMeta[path],
        id: GOAL_GROUPS.CAMPAIGNS_GOALS,
        label: undefined,
      },
      ...goalGroups,
    ];

    const filteredGoals = goals
      .filter(({ goal }) => !!goal?.length)
      .map((group) => {
        const newGoals = group.goal.filter((goal) => !goal.softDeleted);

        return {
          ...group,
          goal: newGoals,
        };
      });

    return filteredGoals;
  }, [columnMeta, preparedRows]);

  const preparedGoalOptions = campaignsGoals.reduce((acc, currentGroup) => {
    const optionsGroup = currentGroup.goal.map((option) => {
      return {
        ...option,
        groupLabel: currentGroup.label,
      };
    });

    return [...acc, ...optionsGroup];
  }, [] as GoalOption[]);

  return preparedGoalOptions;
};

export default usePreparedGoalOptions;
